import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import find from 'lodash/find';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../common/Utils/getFeatureFlag';
import { isEniUspMember } from '../../../common/Utils/memberUtils/memberUtils';
import { useLagoon } from '../../../hooks/useLagoon/useLagoon';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  FacilityDetails,
  FacilityLocationsResponse,
} from '../../../models/FacilityDetails';
import {
  ProviderDetails,
  ProviderLocation,
  ProviderType,
} from '../../../models/ProviderDetails';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useGuestStore } from '../../../store/useGuestStore';
import { getStateFromSearchBar } from '../../../utils/locationSelectors';
import {
  filterNotEmptyHoursOfOperations,
  getLocationAvailabilitySubtext,
  getUncommonElements,
} from '../../../utils/providerDetails.utils';
import { capitalizeAllFirstCharacters } from '../../../utils/providerNames.utils';
import {
  getCurrentMember,
  isCNSMember,
  isMNRMember,
} from '../../../utils/user.utils';
import { Constants, ReverseCoverageTypesCodes } from '../../Constants';
import { phoneOnly } from '../../ConstantsStyles';
import { AllOtherInfos } from './AllOtherInfos/AllOtherInfos';
import { CollapsePhones } from './CollapsePhones';
import { InfoProviderContent } from './InfoProviderContent';
import { OpsHours } from './OpsHours';
import { PhoneNumberList } from './PhoneNumberList';
import { InnerContainer } from './ProviderLocation.style';
import {
  AcceptingNewPatientInfoContainer,
  ViewNetworkAffiliationsLink,
} from './ProviderLocationsDetails.style';

type Props = {
  providerDetails: ProviderDetails | FacilityDetails;
  selectedLocation: ProviderLocation | FacilityLocationsResponse;
  limitedAccessToProvider?: boolean;
};

export const InfoProvider = ({
  providerDetails,
  selectedLocation,
  limitedAccessToProvider,
}: Props) => {
  const { t } = useTranslation();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const { lineOfBusiness, isEniNiceMember, population } = getCurrentMember();
  const isCns = isCNSMember(lineOfBusiness);
  const isMnr = isMNRMember(lineOfBusiness);
  // ENI Nice HMO: isEniNiceMember=true; ENI USP migrated/non-migrated: lob=ENI && population=USP
  const isEni = isEniNiceMember || isEniUspMember(lineOfBusiness, population);
  const isVisionCare = coverageType === ReverseCoverageTypesCodes.VISION;
  const isMedical = coverageType === ReverseCoverageTypesCodes.MEDICAL;
  const isBehavioralHealth =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];

  const { guestData } = useGuestStore(
    useShallow((state) => ({ guestData: state.guestData }))
  );
  const stateCode = guestData?.stateCode || getStateFromSearchBar();
  const isNCState = stateCode === 'NC';

  const { phones, tty, dbaName } = selectedLocation || {};

  const uiMessaging = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();
  const showAcceptsMedicaid = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_ACCEPTS_MEDICAID
  );
  const enableANPForFacility = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ANP_FOR_FACILITIES
  );
  const enableLocationAvailabilityBH = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LOCATION_AVAILABILITY_BH
  );

  const enableNetworkAffiliationFlow = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW
  );

  const showANPInLocationsTab =
    providerDetails.providerType !== ProviderType.ORGANIZATION ||
    isVisionCare ||
    enableANPForFacility;

  const popoverContent = find(uiMessaging, {
    key: ConstantsLagoon.CONTENT_FILTERS.ACCEPTS_MEDICAID_POPOVER_CONTENT,
  })?.message;

  let icon: string = '';
  let acceptingPatientInfo: string = '';

  if (
    selectedLocation?.acceptingNewPatients == null &&
    providerDetails.providerType !== ProviderType.ORGANIZATION
  ) {
    icon = 'do_disturb_alt';
    acceptingPatientInfo = 'PROVIDER_LOCATIONS_DETAILS.NO_INFO_AVAILABLE';
  } else if (selectedLocation?.acceptingNewPatients) {
    icon = 'done';
    acceptingPatientInfo = 'PROVIDER_LOCATIONS_DETAILS.ACCEPTING_PATIENTS';
  } else if (selectedLocation?.acceptingNewPatients === false) {
    icon = 'close';
    acceptingPatientInfo = 'PROVIDER_LOCATIONS_DETAILS.NOT_ACCEPTING_PATIENTS';
  }

  // utils calls
  const notEmptyHoursOfOperation =
    filterNotEmptyHoursOfOperations(selectedLocation);

  const availableDays = notEmptyHoursOfOperation?.map((days) =>
    days.day.toLowerCase()
  );

  const notAvailableDays = getUncommonElements(
    Constants.ALL_DAYS,
    availableDays
  );

  const showDBAName =
    isCns &&
    isNCState &&
    providerDetails.providerType !== ProviderType.PRACTITIONER &&
    isMedical;
  const isPractitioner =
    providerDetails.providerType === ProviderType.PRACTITIONER;

  const shouldShowAcceptingNewPatientsAvailability =
    showANPInLocationsTab &&
    !limitedAccessToProvider &&
    !selectedLocation?.hospitalBasedProviderIndicator;

  const shouldShowLocationAvailability =
    enableLocationAvailabilityBH && isPractitioner && isBehavioralHealth;

  const locationAvailabilitySubtext = shouldShowLocationAvailability
    ? getLocationAvailabilitySubtext(
        t,
        (selectedLocation as ProviderLocation)?.locationAvailability,
        uiMessaging
      )
    : undefined;

  const networks =
    'networks' in selectedLocation ? selectedLocation.networks : undefined;

  const allNetworkAffiliations = (
    networks?.flatMap((network) => network.networkAffiliations || []) || []
  ).filter(
    (affiliation) =>
      affiliation.networkAffiliationId !== null &&
      affiliation.networkAffiliationName !== null
  );

  const shouldShowANPStatusText =
    (isMnr || isEni) &&
    allNetworkAffiliations.length > 0 &&
    enableNetworkAffiliationFlow &&
    (isMedical || isBehavioralHealth);

  return (
    <React.Fragment>
      <InnerContainer>
        {showAcceptsMedicaid && selectedLocation?.isAcceptingMedicaid && (
          <InfoProviderContent
            icon={'done'}
            infoTitle={t('PROVIDER_LOCATIONS_DETAILS.ACCEPTS_MEDICAID')}
            popoverContent={popoverContent}
            popoverTestId="accepts-medicaid-info-icon-popover"
            popoverTitle={
              t('PROVIDER_LOCATIONS_DETAILS.ACCEPTS_MEDICAID') || undefined
            }
            showPopover
            showSubTitle={false}
          />
        )}

        {showDBAName ? (
          <InfoProviderContent
            data-auto-testid="videocam-icon"
            icon="videocam"
            infoSubtext={capitalizeAllFirstCharacters(dbaName?.toLowerCase())}
            infoTitle={t('PROVIDER_LOCATIONS_DETAILS.DBA')}
            showSubTitle
          />
        ) : null}
        {shouldShowANPStatusText &&
        shouldShowAcceptingNewPatientsAvailability ? (
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ width: '20px' }} />
            <AcceptingNewPatientInfoContainer>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'PROVIDER_LOCATIONS_DETAILS.ACCEPTING_NEW_PATIENTS_STATUS_TEXT'
                  ),
                }}
                data-testid="accepting-new-patients-status-text"
              />
              {allNetworkAffiliations && allNetworkAffiliations.length > 0 && (
                <ViewNetworkAffiliationsLink>
                  <a
                    data-testid="view-network-affiliations-link"
                    onClick={() => {
                      const element = document.getElementById(
                        'network-affiliations-section'
                      );
                      element?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    }}
                  >
                    {t('PROVIDER_LOCATIONS_DETAILS.VIEW_NETWORK_AFFILIATIONS')}
                  </a>
                </ViewNetworkAffiliationsLink>
              )}
            </AcceptingNewPatientInfoContainer>
          </div>
        ) : null}
        {!shouldShowANPStatusText &&
        shouldShowAcceptingNewPatientsAvailability ? (
          <InfoProviderContent
            icon={icon}
            iconType="symbol"
            infoTitle={t(acceptingPatientInfo)}
            showSubTitle={false}
          />
        ) : null}
        {shouldShowLocationAvailability ? (
          <InfoProviderContent
            icon="date_range"
            iconType="symbol"
            infoSubtext={locationAvailabilitySubtext}
            infoTitle={t('PROVIDER_LOCATIONS_DETAILS.PROVIDER_AVAILABILITY')}
            showSubTitle
            variant="outlined"
          />
        ) : null}
        {!(notAvailableDays.length >= 7) && (
          <OpsHours selectedLocation={selectedLocation} />
        )}

        {phoneScreen ? (
          <CollapsePhones
            collapseHeader={t('PROVIDER_LOCATIONS_DETAILS.SHOW_MORE_PHONE')}
            phoneNumbers={phones}
            providerDetails={providerDetails as ProviderDetails}
            selectedLocation={selectedLocation}
            tty={tty}
          />
        ) : (
          <PhoneNumberList
            phoneNumbers={phones}
            providerDetails={providerDetails as ProviderDetails}
            selectedLocation={selectedLocation}
            tty={tty}
          />
        )}
      </InnerContainer>

      <AllOtherInfos
        providerDetails={providerDetails}
        selectedLocation={selectedLocation}
      />
    </React.Fragment>
  );
};
